import * as Actions from 'actions'
import { Box } from '@material-ui/core'
import { setActivePreview, setPreviewLoading, setPreviewMode, toggleSplitPreview } from 'actions/preview'
import classNames from 'classnames'
import { PREVIEW_MODE, SPLIT_PREVIEW_BUTTONS } from 'config/constants/preview'
import { ViewSettings } from 'config/constants/editorSettings'
import { SNAPSHOT_SAVE_MODE, getSnapshotSaveMode } from 'config/constants/mainView'
import { TranslationContext } from 'contexts/TranslationContext'
import { PLAYER_TYPE } from 'enums'
import { uploadSnapshot } from 'ServerAPI'
import React, { FC, MouseEvent, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { selectorActivePreview } from 'selectors/preview'
import { selectViewSettings } from 'selectors/mainView'
import { PlayerType } from 'types/common'
import { PreviewModeType } from 'types/preview'
import { useAction } from 'hooks'
import { isRecordingStarted } from 'selectors/recording'
import { ReactComponent as Splitter } from '~/assets/preview/splitter.svg'
import { PreviewPlayerContext } from '../../contexts/PreviewPlayerContext'
import RawButton from '../base/RawButton/RawButton'
import { ReactComponent as CaptureIcon } from '~/assets/video-player/ic_capture.svg'

interface IProps {
 type: string,
 name?:string,
 togglePreview?(e: MouseEvent): void,
 showSplitter: boolean,
 playerType: PlayerType
 isSplitted: boolean,
 activeMode: PreviewModeType,
}

export const PreviewPlayerHead: FC<IProps> = ({ activeMode, type, name, showSplitter, playerType, isSplitted }) => {
  const activePreview = useSelector(selectorActivePreview)
  const mediaRecording = useSelector(isRecordingStarted)
  const viewSettings = useSelector(selectViewSettings)
  const stopRecordingAction = useAction(Actions.recording.stopRecording)
  const { t } = useContext(TranslationContext)
  const { captureFrame, imageSource, videoSource, canvasSource } = useContext(PreviewPlayerContext)
  const [ balloon, setBalloon ] = useState<{ message: string, error: boolean } | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  const dispatch = useDispatch()
  const togglePreview = useCallback(() => {
    dispatch(toggleSplitPreview())
  }, [ dispatch ])

  const onSetPreview = useCallback((mode: PreviewModeType) => () => {
    dispatch(setPreviewMode(mode))
    if (mode !== PREVIEW_MODE.AUTO) {
      let preview
      switch (mode) {
        case PREVIEW_MODE.SOURCE:
          preview = PLAYER_TYPE.MEDIA
          break
        case PREVIEW_MODE.TIMELINE:
          preview = PLAYER_TYPE.TIMELINE
          break
      }
      if (preview !== activePreview) {
        dispatch(setPreviewLoading(true))
        dispatch(setActivePreview(preview))
      }
    }
  }, [ dispatch, activePreview ])

  const captureDisabled = useMemo(() => Boolean(PLAYER_TYPE.MEDIA === playerType
    ? (videoSource || imageSource)
    : canvasSource),
  [ playerType, imageSource, videoSource, canvasSource ])

  const capture = useCallback(() => {
    const isMedia = PLAYER_TYPE.MEDIA === playerType
    if (getSnapshotSaveMode() === SNAPSHOT_SAVE_MODE.UPLOAD) {
      captureFrame(isMedia, dataUrl => {
        abortControllerRef.current?.abort()
        const controller = new AbortController()
        abortControllerRef.current = controller

        uploadSnapshot(dataUrl, controller)
          .then(() => {
            if (controller.signal.aborted) return
            setBalloon({ message: t('SNAPSHOT_STORED'), error: false })
          })
          .catch(err => {
            if (controller.signal.aborted) return
            setBalloon({
              message: err?.response?.data?.error || err?.message || String(err),
              error: true,
            })
          })
      })
    } else {
      captureFrame(isMedia)
    }
  }, [ captureFrame, playerType, t ])

  useEffect(() => {
    if (!balloon) return undefined
    const timer = setTimeout(() => setBalloon(null), 2000)
    return () => clearTimeout(timer)
  }, [ balloon ])

  const onClockButtonBlock = () => {
    if (mediaRecording) {
      stopRecordingAction()
    }
  }

  return (
    <div className="head__item item" style={{ userSelect: 'none' }}>
      <Box
        display="flex"
        alignItems="center"
        gridGap="10px"
        className={classNames('item__box', { active: activePreview === playerType })}
      >
        <span className={classNames('item__type', { active: activePreview === playerType })}>
          {type}
        </span>
        <span className="item__name">{name || ''}</span>
      </Box>
      <div className="head__item__left" onClick={onClockButtonBlock}>
        {Boolean(viewSettings[ViewSettings.showPreviewSwitchButtons]) && (
          <Box className="item__preview-splitting preview-splitting">
            <Box className={classNames('preview-splitting__buttons button', { hidden: isSplitted })}>
              {SPLIT_PREVIEW_BUTTONS.map(({ name, mode }) => (
                <Box
                  className={classNames('button__item', { active: mode === activeMode })}
                  onClick={onSetPreview(mode as PreviewModeType)}
                  key={mode}
                >
                  {t(name)}
                </Box>
              ))}
            </Box>
          </Box>
        )}
        {(!isSplitted || showSplitter)
          && Boolean(viewSettings[ViewSettings.showPreviewSplitButton])
          && <Box onClick={togglePreview} className="preview-splitting__splitter"><Splitter /></Box>}

        {(__APP_PROFILE__ === 'public' || __APP_PROFILE__ === 'default') && (
          <div className="preview-frame-capture" title={t('PREVIEW_BTN_FRAME_CAPTURE_TOOLTIP')}>
            <RawButton
              dataLangId="PREVIEW_BTN_FRAME_CAPTURE"
              onClick={capture}
              disabled={!captureDisabled}
              ariaLabel={t('PREVIEW_BTN_FRAME_CAPTURE_TOOLTIP')}
            >
              <CaptureIcon />
            </RawButton>
            {balloon && (
              <div
                className={classNames('snapshot-balloon', { 'snapshot-balloon--error': balloon.error })}
                onClick={() => setBalloon(null)}
              >
                {balloon.message}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
